import { Injectable, InjectionToken, inject } from '@angular/core';

import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { UtilityService } from '../../utils/utility.service';
import { PreferencesRestService } from './preferences-rest.service';
import { AccountPreferences, ExtraParametersPreferences, UserPreferences } from './preferences.domain';

export interface PreferencesConfiguration {
  readonly user: UserPreferences;
  readonly account: AccountPreferences;
}

export const PREFERENCES_CONFIGURATION = new InjectionToken<PreferencesConfiguration>('preferencesConfiguration');

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private readonly preferencesRestService = inject(PreferencesRestService);

  readonly onResponsivePreferenceChange: Subject<boolean>;
  readonly onPreferenceValueChange: BehaviorSubject<void>;
  private readonly preloadedUserPreferences: UserPreferences;
  private userPreferences: UserPreferences;
  private accountPreferences: AccountPreferences;

  private sharedUserPreference: Observable<UserPreferences>;
  private sharedAccountPreference: Observable<AccountPreferences>;

  constructor() {
    const configuration = inject<PreferencesConfiguration>(PREFERENCES_CONFIGURATION);

    this.preloadedUserPreferences = configuration.user === undefined ? {} : configuration.user;
    this.userPreferences = Object.assign({}, this.preloadedUserPreferences);
    this.accountPreferences = configuration.account === undefined ? {} : configuration.account;
    this.onResponsivePreferenceChange = new Subject<boolean>();
    this.onPreferenceValueChange = new BehaviorSubject<void>(undefined);
  }

  /**
   * Get preloaded user preference
   *
   * @returns: value of a preference key
   * @param preferenceKey
   */
  getPreloadedUserPreference<T>(preferenceKey: string): T {
    return UtilityService.deepClone(this.userPreferences[preferenceKey]);
  }

  /**
   * Get single user preference
   *
   * @returns any: user preference value
   * @param preferenceKey
   */
  getUserPreference<T>(preferenceKey: string): Observable<T> {
    return this.getUserPreferences().pipe(
      map((preferences: UserPreferences): T => UtilityService.deepClone(preferences[preferenceKey])));
  }

  /**
   * Get user preference
   * Load from server, if user perferences are not in preload preferences
   *
   * If preferenceKeys is undefined returns data from server
   * @returns: user preferences
   */
  getUserPreferences(): Observable<UserPreferences> {
    if (this.userPreferences === undefined) {
      if (this.sharedUserPreference === undefined) {
        this.sharedUserPreference = this.preferencesRestService.getUserPreferences().pipe(shareReplay(1));
      }
      return this.sharedUserPreference.pipe(map((preferences: UserPreferences): UserPreferences => {
        this.userPreferences = preferences === undefined ? {} : preferences;
        return this.getCachedUserPreferences();
      }));
    } else {
      return of(this.getCachedUserPreferences());
    }
  }

  /**
   * Update user preference.
   * Note: Doesn't call back end to get new updated user preferences. If the update is successful, cached user
   * preferences (this.userPreferences) are updated with new values without retrieving new values from the back end.
   *
   * @returns: user preferences
   * @param userPreferences
   * @param extraParameters
   */
  updateUserPreferences(userPreferences: UserPreferences, extraParameters?: ExtraParametersPreferences): Observable<UserPreferences> {
    return this.preferencesRestService.updateUserPreferences(userPreferences, extraParameters)
      .pipe(
        map((): UserPreferences => this.updateUserPreferencesCache(userPreferences)));
  }

  toggleResponsivePreference(): Observable<UserPreferences> {
    return this.getUserPreferences()
      .pipe(
        switchMap((userPreferences: UserPreferences): Observable<UserPreferences> => {
          userPreferences.USING_RESPONSIVE = !userPreferences.USING_RESPONSIVE;
          return this.updateUserPreferences({ USING_RESPONSIVE: userPreferences.USING_RESPONSIVE });
        }),
        switchMap((userPreferences: UserPreferences): Observable<UserPreferences> => {
          this.onResponsivePreferenceChange.next(userPreferences.USING_RESPONSIVE as boolean);
          return of(userPreferences);
        })
      );
  }

  /**
   * Update user preference cache only.
   *
   * @returns: user preferences
   * @param userPreferences
   */
  updateUserPreferencesCache(userPreferences: UserPreferences): UserPreferences {
    this.userPreferences = UtilityService.deepExtend(this.userPreferences, userPreferences);
    this.onPreferenceValueChange.next();
    return this.getCachedUserPreferences();
  }

  /**
   * Refresh user preferences
   *
   * @returns: user preferences
   */
  refreshUserPreferences(): void {
    this.userPreferences = undefined;
    this.sharedUserPreference = undefined;
    this.getUserPreferences().subscribe();
  }

  /**
   * Get account preference
   *
   * If preference key is not preloaded, will load from server
   * @returns: account preferences
   */
  getAccountPreferences(): Observable<AccountPreferences> {
    if (this.accountPreferences !== undefined) {
      return of(this.getCachedAccountPreferences());
    }
    if (this.sharedAccountPreference === undefined) {
      this.sharedAccountPreference = this.preferencesRestService.getAccountPreferences().pipe(shareReplay(1));
    }
    return this.sharedAccountPreference
      .pipe(
        map((preferences: AccountPreferences): AccountPreferences => {
          this.accountPreferences = preferences === undefined ? {} : preferences;
          return this.getCachedAccountPreferences();
        }));
  }

  /**
   * Update account preference. If the update is successful, cached account
   * preferences (this.accountPreferences) are updated with new values without retrieving new values from the back end.
   *
   * @returns: account preferences
   * @param accountPreferences
   */
  updateAccountPreferences(accountPreferences: AccountPreferences): Observable<AccountPreferences> {
    return this.preferencesRestService.updateAccountPreferences(accountPreferences)
      .pipe(
        map((): AccountPreferences => {
          this.accountPreferences = UtilityService.deepExtend(this.accountPreferences, accountPreferences);
          return this.getCachedAccountPreferences();
        }));
  }

  /**
   * Refresh account preferences
   *
   * @returns: account preferences
   */
  refreshAccountPreferences(): Observable<AccountPreferences> {
    this.accountPreferences = undefined;
    this.sharedAccountPreference = undefined;
    return this.getAccountPreferences();
  }

  private getCachedUserPreferences(): UserPreferences {
    return UtilityService.deepClone(this.userPreferences);
  }

  private getCachedAccountPreferences(): AccountPreferences {
    return UtilityService.deepClone(this.accountPreferences);
  }
}
